// Supprimer ses propres créations (D-178) : un drill, un exercice, un jeu ou un paquet de cartes. Avant de supprimer, une
// ligne dit où c'est utilisé (« Utilisé dans 2 séances à venir et 5 passées : rien n'y change. ») ; les séances gardent
// l'élément tel quel, marqué « supprimé de la bibliothèque ».

export type LibKind = 'drill' | 'exercise' | 'game' | 'deck';

/** Ce que rend library_usage (des nombres seulement). */
export interface LibUsage {
  upcoming: number; past: number; templates: number; programs: number; themes: number; exercises: number; games: number;
  objectives: number; libraries: number; proposed: boolean; runs: number;
}

export const ARCHIVED_LABEL = 'supprimé de la bibliothèque';

/** Le nom de chaque genre, pour les messages (« Drill supprimé », « Jeu remis »). */
export const LIB_NOUN: Record<LibKind, string> = { drill: 'Drill', exercise: 'Exercice', game: 'Jeu', deck: 'Paquet' };

/** Relu sans confiance : un nombre manquant vaut 0. */
export function readUsage(u: unknown): LibUsage {
  const o = (u && typeof u === 'object' ? u : {}) as Record<string, unknown>;
  const num = (k: string) => (typeof o[k] === 'number' && Number.isFinite(o[k]) ? Math.max(0, Math.floor(o[k] as number)) : 0);
  return {
    upcoming: num('upcoming'), past: num('past'), templates: num('templates'), programs: num('programs'), themes: num('themes'),
    exercises: num('exercises'), games: num('games'), objectives: num('objectives'), libraries: num('libraries'), proposed: o.proposed === true, runs: num('runs'),
  };
}

const many = (n: number, one: string, more: string) => `${n} ${n > 1 ? more : one}`;
const list = (xs: string[]) => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} et ${xs[xs.length - 1]}`);

/** La ligne « où c'est utilisé », dite au premier toucher sur « Supprimer ». */
export function usageLine(kind: LibKind, u: LibUsage): string {
  if (kind === 'deck') return 'Les jeux déjà préparés gardent leurs cartes.';
  if (kind === 'game') return u.runs ? `Joué ${u.runs} fois : les résultats restent.` : 'Jamais joué pour l’instant.';
  const out: string[] = [];
  const sessions: string[] = [];
  if (u.upcoming && u.past) sessions.push(`${many(u.upcoming, 'séance à venir', 'séances à venir')} et ${u.past} passée${u.past > 1 ? 's' : ''}`);
  else if (u.upcoming) sessions.push(many(u.upcoming, 'séance à venir', 'séances à venir'));
  else if (u.past) sessions.push(many(u.past, 'séance passée', 'séances passées'));
  if (u.templates) sessions.push(many(u.templates, 'séance type', 'séances types'));
  if (sessions.length) out.push(`Utilisé dans ${list(sessions)} : rien n’y change.`);
  const also: string[] = [];
  if (u.exercises) also.push(many(u.exercises, 'exercice', 'exercices'));
  if (u.games) also.push(many(u.games, 'jeu', 'jeux'));
  if (u.themes) also.push('le thème du mois');
  if (u.objectives) also.push(many(u.objectives, 'objectif du projet sportif', 'objectifs du projet sportif'));
  if (also.length) out.push(`${sessions.length ? 'Aussi dans' : 'Dans'} ${list(also)}.`);
  if (u.programs) out.push(`Dans ${many(u.programs, 'prépa', 'prépas')} : il n’y sera plus.`);
  if (u.proposed) out.push('Proposé au club : les autres coachs ne le verront plus dans la bibliothèque.');
  else if (u.libraries) out.push(u.libraries > 1 ? `Dans la bibliothèque de ${u.libraries} autres coachs : ils ne le verront plus.` : 'Dans la bibliothèque d’un autre coach : il ne le verra plus.');
  return out.length ? out.join(' ') : 'Utilisé nulle part pour l’instant.';
}
